import classNames from 'classnames'
import _ from 'lodash'
import IMask from 'imask'
import React from 'react'
import { v4 as uuidv4 } from 'uuid'


import type { UI2ScanFieldProps } from 'shared-libs/components/ui2/fields/ui2-scan-field/ui2-scan-field-props'
import type { IUI2WebFieldBaseExtras } from '../field-ui/i-ui2-web-field-base-extras'
import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { UI2EmptyStateLauncher } from 'browser/components/ui2/fields/field-ui/empty-state-launcher/empty-state-launcher'
import { UI2PlatformContext as FormFieldPlatformContext } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import { UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2Banner } from 'browser/components/ui2/display/ui2-banner/ui2-banner'
import { UI2NoValueAvailable } from '../field-ui/no-value-available/no-value-available'
import { IMASK_DEFINITIONS, parseMaskToImask } from '../ui2-input-field/ui2-input-mask'
import { BarcodeScanner } from 'browser/mobile/components/barcode/barcode-scanner-modal'
import { Modal } from 'browser/components/atomic-elements/atoms/modal'
import { translateString } from 'shared-libs/helpers/utils'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_ui2-scan-field.scss'

// ─── FileReader helper ────────────────────────────────────────────────────────

function readBlobAsDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error || new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_SUCCESS_MESSAGE = 'Scanned successfully'
const DEFAULT_FAILURE_MESSAGE = 'Scan unsuccessful'
const DEFAULT_RETRY_LABEL = 'Retry'

const DEFAULT_LAUNCHER_MESSAGE: Record<string, string> = {
  barcode: 'Tap to scan barcode',
  qr: 'Tap to scan QR code',
}

const DEFAULT_PLACEHOLDER: Record<string, string> = {
  barcode: 'Scan barcode',
  qr: 'Scan QR code',
  // No camera on web for text, so nothing here suggests one.
  text: 'Enter number',
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface IUI2ScanFieldProps extends UI2ScanFieldProps, IUI2WebFieldBaseExtras {}

// ─── State ────────────────────────────────────────────────────────────────────

interface IUI2ScanFieldState {
  internalValue: string
  isLightboxOpen: boolean
}

// ─── Component ────────────────────────────────────────────────────────────────

export class UI2ScanField extends React.Component<IUI2ScanFieldProps, IUI2ScanFieldState> {
  static contextType = FormFieldPlatformContext
  declare context: React.ContextType<typeof FormFieldPlatformContext>

  private inputRef = React.createRef<HTMLInputElement>()
  private maskInstance: any = null
  private _scannerVisible = false

  constructor(props: IUI2ScanFieldProps) {
    super(props)
    this.state = {
      internalValue: props.value != null ? String(props.value) : '',
      isLightboxOpen: false,
    }
  }

  public componentDidMount(): void {
    const { textMask, openCaptureOnMount } = this.props
    if (textMask) this.initMask()
    if (openCaptureOnMount && this.isMobileWeb() && !this.isTextScan()) {
      this._scannerVisible = true
      this.forceUpdate()
    }
  }

  public componentWillUnmount(): void {
    document.removeEventListener('keydown', this.handleLightboxKeyDown)
    this.destroyMask()
  }

  public componentDidUpdate(prevProps: Readonly<IUI2ScanFieldProps>): void {
    const { textMask, value } = this.props
    if (prevProps.value !== value) {
      this.setState({ internalValue: value != null ? String(value) : '' })
    }
    if (prevProps.textMask !== textMask) {
      this.destroyMask()
      if (textMask) this.initMask()
    } else if (this.maskInstance && value !== prevProps.value) {
      this.maskInstance.value = value != null ? String(value) : ''
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────────

  private renderEditableContent(): React.ReactNode {
    const { hideCapturedValue } = this.props
    const { internalValue } = this.state
    if (hideCapturedValue && !_.isEmpty(internalValue) && this.isMobileWeb()) {
      return this.renderConditionResult()
    }
    // Text mode has no camera on web (no OCR engine in the bundle), so there is
    // nothing for a launcher to launch — it is a plain input on DW and MW alike.
    if (this.isMobileWeb() && _.isEmpty(internalValue) && !this.isTextScan()) {
      return this.renderMWEmptyLauncher()
    }
    return this.renderInput()
  }

  private renderReadOnly = (value: any, { editNode }: { editNode?: React.ReactNode }): React.ReactNode => {
    return (
      <>
        <div className='ui2-fieldShell-readOnly-content'>{this.renderReadOnlyRow()}</div>
        {editNode}
      </>
    )
  }

  private renderInput() {
    const { placeholder, scanType, isDisabled, frames } = this.props
    const { internalValue } = this.state
    const effectivePlaceholder = placeholder ?? DEFAULT_PLACEHOLDER[scanType ?? 'barcode']
    // The intent icon is decoration that says what this field scans. Drop it in
    // text mode (a scan glyph would advertise a capability web doesn't have),
    // and drop it whenever the rescan button is showing the very same glyph as
    // an actual action — two identical icons flanking the value read as a pair
    // of buttons, one of which does nothing.
    const intentIcon = this.isTextScan() || this.showsRescanButton()
      ? undefined
      : scanType === 'qr'
        ? <UI2Icon name={UI2IconNames.SCAN_QR_CODE} size={14} className='ui2-scanField-intentIcon' />
        : <UI2Icon name={UI2IconNames.SCAN_BARCODE} size={14} className='ui2-scanField-intentIcon' />
    return (
      <UI2FieldControl isDisabled={isDisabled} rightAddon={this.renderRightAddon()} leftContent={intentIcon} frames={frames as any}>
        <input
          ref={this.inputRef}
          type='text'
          value={internalValue}
          onChange={this.handleChange}
          placeholder={effectivePlaceholder}
          disabled={isDisabled}
        />
      </UI2FieldControl>
    )
  }

  private renderRightAddon() {
    const capturedImage = this.getCapturedImage()
    return (
      <>
        {this.renderClearButton()}
        {this.renderRescanButton()}
        {capturedImage && this.renderImageThumbnail(capturedImage)}
      </>
    )
  }

  /**
   * Re-open the scanner on a field that already holds a value. This used to be
   * the job of the image placeholder, which doubled as an "you owe a photo"
   * cue; the photo is no longer this field's concern, but the way back into the
   * scanner still is. MW only — DW has no camera.
   */
  private showsRescanButton(): boolean {
    const { isDisabled, isReadOnly } = this.props
    return this.isMobileWeb() && !this.isTextScan() && !isDisabled && !isReadOnly
  }

  private renderRescanButton() {
    const { scanType } = this.props
    if (!this.showsRescanButton()) return null
    const scanIconName = scanType === 'qr' ? UI2IconNames.SCAN_QR_CODE : UI2IconNames.SCAN_BARCODE
    return (
      <button
        className='ui2-scanField-rescanButton'
        onClick={this.handleMobileEmptyPress}
        aria-label={scanType === 'qr' ? 'Scan QR code again' : 'Scan barcode again'}
      >
        <UI2Icon name={scanIconName} size={18} />
      </button>
    )
  }

  private renderClearButton() {
    const { isDisabled, isReadOnly, hideCapturedValue } = this.props
    const { internalValue } = this.state
    if (isDisabled || isReadOnly) return null
    if (hideCapturedValue && !_.isEmpty(internalValue)) return null
    const hasImage = !!this.getCapturedImage()
    if (_.isEmpty(internalValue) && !hasImage) return null
    return (
      <button
        className='ui2-scanField-clearButton'
        onClick={this.handleClear}
        aria-label={`Clear ${this.props.scanType === 'qr' ? 'QR code' : 'value'}`}
      >
        <UI2Icon name={UI2IconNames.X} size={12} />
      </button>
    )
  }

  private renderImageThumbnail(image: any) {
    const uri = this.getImageUri(image)
    if (!uri) return null
    return (
      <button className='ui2-scanField-imageThumbnailButton' onClick={this.handleOpenLightbox}>
        <img className='ui2-scanField-imageThumbnail' src={uri} alt='Captured' />
      </button>
    )
  }

  private renderLightbox(): React.ReactNode {
    const { isLightboxOpen } = this.state
    if (!isLightboxOpen) return null
    const capturedImage = this.getCapturedImage()
    if (!capturedImage) return null
    const uri = this.getImageUri(capturedImage)
    return (
      <div className='ui2-scanField-lightbox' role='dialog' aria-modal>
        <div className='ui2-scanField-lightbox-backdrop' onClick={this.handleCloseLightbox} />
        {uri && <img className='ui2-scanField-lightbox-image' src={uri} alt='Captured' />}
        <button className='ui2-scanField-lightbox-close' onClick={this.handleCloseLightbox} aria-label='Close'>
          <UI2Icon name={UI2IconNames.X} />
        </button>
      </div>
    )
  }

  private renderMWEmptyLauncher(): React.ReactNode {
    const { scanType, placeholder, translationTable } = this.props
    const defaultMsg = DEFAULT_LAUNCHER_MESSAGE[scanType ?? 'barcode']
    const icon = scanType === 'qr'
      ? <UI2Icon name={UI2IconNames.SCAN_QR_CODE} size={16} />
      : <UI2Icon name={UI2IconNames.SCAN_BARCODE} size={16} />
    return (
      <UI2EmptyStateLauncher
        onPress={this.handleMobileEmptyPress}
        message={translateString(placeholder || defaultMsg, translationTable) ?? defaultMsg}
        icon={icon}
      />
    )
  }

  private renderConditionResult(): React.ReactNode {
    return this.isConditionSatisfied() ? this.renderSuccessIndicator() : this.renderFailureIndicator()
  }

  private renderSuccessIndicator(): React.ReactNode {
    const { successMessage, translationTable, frames } = this.props
    return (
      <UI2Banner
        mode='success'
        title={DEFAULT_SUCCESS_MESSAGE}
        text={successMessage ? (translateString(successMessage, translationTable) ?? successMessage) : undefined}
        frames={frames}
      />
    )
  }

  private renderFailureIndicator(): React.ReactNode {
    const { failureMessage, translationTable, frames, isReadOnly } = this.props
    const canRetry = !isReadOnly
    return (
      <UI2Banner
        mode='danger'
        title={DEFAULT_FAILURE_MESSAGE}
        text={failureMessage ? (translateString(failureMessage, translationTable) ?? failureMessage) : undefined}
        frames={frames}
      >
        {canRetry && (
          <button className='ui2-scanField-retryButton' onClick={this.handleRetry}>
            {translateString(DEFAULT_RETRY_LABEL, translationTable)}
          </button>
        )}
      </UI2Banner>
    )
  }

  private renderReadOnlyRow(): React.ReactNode {
    const { value } = this.props
    const capturedImage = this.getCapturedImage()
    const hasValue = value != null && value !== ''
    return (
      <div className='ui2-scanField-readOnlyRow'>
        {capturedImage && this.renderImageThumbnail(capturedImage)}
        {hasValue
          ? <span className='ui2-scanField-readOnlyValue'>{String(value)}</span>
          : <UI2NoValueAvailable />
        }
      </div>
    )
  }

  // ─── Event handlers ───────────────────────────────────────────────────────────

  private handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    this.setState({ internalValue: newValue })
    this.props.onChange?.(newValue || undefined)
    if (!newValue) this.clearImage()
  }

  private handleClear = () => {
    this.setState({ internalValue: '' })
    if (this.maskInstance) this.maskInstance.value = ''
    this.props.onChange?.(undefined)
    this.clearImage()
  }

  private handleMobileEmptyPress = () => {
    this._scannerVisible = true
    this.forceUpdate()
  }

  private handleScanCapture = (decoded: string) => {
    this.setState({ internalValue: decoded })
    this.props.onChange?.(decoded)
    this._scannerVisible = false
    this.forceUpdate()
  }

  private handleScanCaptureImage = async (blob: Blob, mimeType: string) => {
    const { entity, onChange } = this.props
    if (!entity) return
    const imagePath = this.normalizeImagePath()
    if (!imagePath) return
    try {
      const dataUrl = await readBlobAsDataURL(blob)
      const filename = `scan-${uuidv4()}.${mimeType.split('/')[1] || 'jpg'}`
      const file = new File([blob], filename, { type: mimeType })
      entity.clearMultipartFiles(imagePath)
      entity.addMultipartFiles(imagePath, [file])
      onChange?.(
        { name: filename, uri: dataUrl, type: mimeType },
        false,
        imagePath.join('.'),
      )
    } catch (e) {
      console.error('[ui2-scanField] image capture failed', e)
    }
  }

  private handleOpenLightbox = () => {
    this.setState({ isLightboxOpen: true })
    document.addEventListener('keydown', this.handleLightboxKeyDown)
  }

  private handleCloseLightbox = () => {
    this.setState({ isLightboxOpen: false })
    document.removeEventListener('keydown', this.handleLightboxKeyDown)
  }

  private handleLightboxKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.handleCloseLightbox()
  }

  private handleRetry = () => {
    this.setState({ internalValue: '' })
    this.props.onChange?.(undefined)
  }

  // ─── Mask (barcode DW only) ───────────────────────────────────────────────────

  private initMask() {
    const { textMask, value } = this.props
    if (!textMask || !this.inputRef.current) return
    this.maskInstance = IMask(this.inputRef.current, {
      mask: parseMaskToImask(textMask),
      definitions: IMASK_DEFINITIONS,
    })
    this.maskInstance.value = value != null ? String(value) : ''
    this.maskInstance.on('accept', this.handleMaskAccept)
  }

  private destroyMask() {
    if (!this.maskInstance) return
    this.maskInstance.off('accept', this.handleMaskAccept)
    this.maskInstance.destroy()
    this.maskInstance = null
  }

  private handleMaskAccept = () => {
    const { maskStoredAs, onChange } = this.props
    const raw = this.maskInstance?.unmaskedValue ?? ''
    const formatted = this.maskInstance?.value ?? ''
    const emitValue = maskStoredAs === 'formatted' ? formatted : raw
    this.setState({ internalValue: formatted })
    onChange?.(emitValue || undefined)
    if (!emitValue) this.clearImage()
  }

  // ─── Utilities ────────────────────────────────────────────────────────────────

  private isMobileWeb() {
    return this.context === 'mobileWeb'
  }

  private isTextScan() {
    return this.props.scanType === 'text'
  }

  private getCapturedImage() {
    const { entity } = this.props
    const imagePath = this.normalizeImagePath()
    if (!entity || !imagePath) return undefined
    const files = entity.get(imagePath)
    return Array.isArray(files) && files.length > 0 ? files[0] : undefined
  }

  private getImageUri(image: any): string | undefined {
    if (!image) return undefined
    if (typeof image.uri === 'string') return image.uri
    if (Array.isArray(image.source) && image.source[0]?.uri) return image.source[0].uri
    return undefined
  }

  private clearImage() {
    const { entity, onChange } = this.props
    const imagePath = this.normalizeImagePath()
    if (!entity || !imagePath) return
    entity.clearMultipartFiles(imagePath)
    onChange?.(undefined, false, imagePath.join('.'))
  }

  /**
   * Returns undefined in text mode, which is the single gate that makes
   * `imagePath` a no-op there: no capture, no thumbnail, no lightbox, nothing
   * to clear. The field only claims a photo on a platform where it can take
   * one, and web text mode has no camera.
   */
  private normalizeImagePath(): string[] | undefined {
    const { imagePath, frames } = this.props
    if (!imagePath || this.isTextScan()) return undefined
    const segments = imagePath.split('.')
    const valuePath: string[] = (frames as any)?.getContext?.('valuePath') ?? []
    const parentIndex = _.findLastIndex(valuePath, (v) => typeof v === 'number')
    return !valuePath.length || parentIndex === -1
      ? segments
      : [..._.slice(valuePath, 0, parentIndex + 1), ...segments]
  }

  private isConditionSatisfied() {
    const { successCondition, frames } = this.props
    if (!successCondition || !frames) return true
    try {
      const { evaluateExpressionWithScopes } = require('shared-libs/helpers/evaluation')
      const { CustomFormulas } = require('shared-libs/helpers/formulas')
      return !!evaluateExpressionWithScopes(frames as any, successCondition, CustomFormulas)
    } catch {
      return false
    }
  }

  // ─── Main render ──────────────────────────────────────────────────────────────

  public render() {
    const { isReadOnly, hideCapturedValue, enablePrefilledMode, barcodeTypes, scanType } = this.props
    const { internalValue } = this.state
    const isMW = this.isMobileWeb()
    const hasValue = !_.isEmpty(internalValue)
    const isResultState = !!(hideCapturedValue && hasValue && !isReadOnly && isMW)
    // Honour the layout the form injected. This used to be
    // `isReadOnly && !isResultState`, which forced every editable code-scan
    // field to render vertically no matter what the surrounding form asked
    // for. The result state stays vertical on purpose — its body is a status
    // row rather than an input, so it doesn't belong in the control cell.
    const isHorizontalLayout = !!this.props.isHorizontalLayout && !isResultState
    const showEmpty = isMW && !hasValue && !isReadOnly && !this.isTextScan()

    const rootCls = classNames('ui2-scanField', {
      'ui2-scanField--isEmpty': showEmpty,
      'ui2-scanField--isResult': isResultState,
    })

    const scannerModal = this._scannerVisible ? (
      <Modal
        onClose={() => { this._scannerVisible = false; this.forceUpdate() }}
        isOpen
        modalDialogClassName='pa0 pt1 vh-100'
        modalPaperClassName='h-100'
        modalBackdropClassName=''
        modalStyle={{ padding: 0, width: '100%', height: '100%' }}
      >
        <BarcodeScanner
          barcodeTypes={scanType === 'qr' ? ['qr'] : barcodeTypes}
          onCapture={this.handleScanCapture}
          onCaptureImage={this.normalizeImagePath() ? this.handleScanCaptureImage : undefined}
          onClose={() => { this._scannerVisible = false; this.forceUpdate() }}
          helpText={scanType === 'qr' ? 'QR_Code_Field_Default_Help_Text' : undefined}
        />
      </Modal>
    ) : null

    return (
      <>
        <UI2FieldShell
          {...this.props}
          className={rootCls}
          enablePrefilledMode={!!enablePrefilledMode}
          // False in the result state: the body there is a status banner, not a
          // UI2FieldControl, and the flag is a declaration about what the body
          // actually is — the shell routes the prefilled Cancel and the inline
          // help into the control on the strength of it.
          bodyIsFieldControl={!isResultState}
          renderReadOnly={this.renderReadOnly}
          isHorizontalLayout={isHorizontalLayout}
        >
          {this.renderEditableContent()}
        </UI2FieldShell>
        {this.renderLightbox()}
        {scannerModal}
      </>
    )
  }
}
